<template>
  <base-form :title="$t('client.financialInformation')"
             :loading="loading"
             @submit="submitForm"
             class="row d-flex">
    <div class="row">
      <div class="col-12 col-md-6">
        <fg-input :placeholder="$t('client.management.edit.invoice_email')"
                  :name="$t('client.management.edit.invoice_email')"
                  :error="getError($t('client.management.edit.invoice_email'))"
                  :label="$t('client.management.edit.invoice_email')"
                  v-validate="'email'"
                  v-model="clientToEdit.invoice_email">
        </fg-input>
      </div>
      <div class="col-12 col-md-6">
        <fg-input :placeholder="$t('client.management.edit.external_reference')"
                  :name="$t('client.management.edit.external_reference')"
                  :label="$t('client.management.edit.external_reference')"
                  v-model="clientToEdit.external_reference">
        </fg-input>
      </div>
      <div class="col-12 col-md-6">
        <fg-input :placeholder="$t('client.management.edit.vat_number')"
                  :name="$t('client.management.edit.vat_number')"
                  :label="$t('client.management.edit.vat_number')"
                  v-model="clientToEdit.vat_number">
        </fg-input>
      </div>
      <div class="col-12 col-md-6">
        <fg-input :placeholder="$t('client.management.edit.vat_percentage')"
                  :name="$t('client.management.edit.vat_percentage')"
                  :error="getError($t('client.management.edit.vat_percentage'))"
                  :label="$t('client.management.edit.vat_percentage')"
                  type="number"
                  v-validate="modelValidations.vat_percentage"
                  v-model="clientToEdit.vat_percentage">
          <small slot="addonRight">%</small>
        </fg-input>
      </div>
      <div class="col-4">
        <fg-input :label="$t('client.management.edit.automatic_verify_orders')">
          <base-switch v-model="clientToEdit.automatic_verify_orders"></base-switch>
        </fg-input>
      </div>
      <div class="col-4">
        <fg-input :label="$t('client.management.edit.allowed_to_create_organisations')">
          <base-switch v-model="clientToEdit.allowed_to_create_organisations"></base-switch>
        </fg-input>
      </div>
      <div class="col-4">
        <fg-input :label="$t('client.management.edit.invoice_automatically')">
          <base-switch v-model="clientToEdit.invoice_automatically"></base-switch>
        </fg-input>
      </div>
      <template v-if="clientToEdit.invoice_automatically">
        <div class="col-6">
          <fg-input :label="$t('invoiceStrategy.placeholder')">
            <invoice-strategy-select v-model="clientToEdit.invoice_strategy">
            </invoice-strategy-select>
          </fg-input>
        </div>
        <div class="col-6">
          <fg-input :placeholder="$t('client.management.edit.invoice_strategy_amount')"
                    :label="$t('client.management.edit.invoice_strategy_amount')"
                    type="number"
                    v-model="clientToEdit.invoice_strategy_amount">
            <small slot="addonRight">{{currencySymbol}}</small>
          </fg-input>
        </div>
      </template>
    </div>

    <div class="row" v-for="(address, index) in clientToEdit.addresses" :key="`addresses.${index}`">
      <template v-if="address.financial">
        <div class="col-12">
          <section-separator has-border size="lg">
            {{$t('generic.addressAndPhone')}}
          </section-separator>
        </div>
        <div class="col-12">
          <fg-input :placeholder="$t('client.management.edit.address')"
                    :label="$t('client.management.edit.address')"
                    id="city-locality"
                    :enable-geolocation="true"
                    component="vue-google-autocomplete"
                    name="job city"
                    v-model="address.address_line_1"
                    @placechanged="(data, place) => onAddressChange(data, place, index)"
                    addon-left-icon="nc-icon nc-pin-3">
          </fg-input>
        </div>
        <div class="col-12 col-sm-6">
          <fg-input :name="$t('client.management.edit.postal_code')"
                    :label="$t('client.management.edit.postal_code')"
                    :placeholder="$t('client.management.edit.postal_code')"
                    v-model="address.postal_code">
          </fg-input>
        </div>
        <div class="col-12 col-sm-6">
          <fg-input :name="$t('generic.city')"
                    :placeholder="$t('generic.city')"
                    :label="$t('generic.city')"
                    v-model="address.city">
          </fg-input>
        </div>
        <div class="col-12 col-sm-6">
          <fg-input :label="$t('generic.country')">
            <country-autocomplete
              :name="$t('generic.country')"
              :placeholder="$t('generic.country')"
              v-model="address.country_code">
            </country-autocomplete>
          </fg-input>
        </div>
      </template>
    </div>
    <div class="row" v-for="(phone, index) in clientToEdit.phones" :key="`phones.${index}`">
      <div class="col-12" v-show="phone.financial">
        <fg-input :label="$t('client.management.edit.phone')">
          <phone-input :name="$t('client.management.edit.phone')"
                       @input-data="(data) => onPhoneChange(data, index)"
                       :detect-country="mode === 'add'"
                       v-model="phone.phone">
          </phone-input>
        </fg-input>
      </div>
    </div>
  </base-form>
</template>
<script>
  import Vue from 'vue'
  import { Select, Option } from 'element-ui'
  import VueGoogleAutocomplete from 'src/components/Autocomplete/VueGoogleAutocomplete'
  import InvoiceStrategySelect from 'src/components/Autocomplete/InvoiceStrategySelect'
  import { Validator } from 'vee-validate'
  import ImageUpload from 'src/components/ImageUpload'
  import BaseAvatar from 'src/components/BaseAvatar'
  import PhoneInput from 'src/components/PhoneInput'
  import { clientStatus, clientType } from 'src/config/clientEnums'
  import { findCountryCode } from 'src/utils/googleAutocompleteUtils'
  import CountryAutocomplete from 'src/components/Autocomplete/CountryAutocomplete'

  Vue.component(VueGoogleAutocomplete.name, VueGoogleAutocomplete)
  let emptyAddress = {
    postal_code: '',
    city: '',
    country: '',
    country_code: '',
    state_of_province: '',
    address_line_1: '',
    financial: 1,
    primary: null
  }
  let emptyPhone = {
    country_code: '',
    phone: '',
    financial: 1,
    label: 'Default',
    primary: null
  }

  let phoneValidation = (value, args) => {
    return args[0]() || false
  }
  Validator.extend('phone', phoneValidation)
  export default {
    name: 'client-financial-form',
    components: {
      BaseAvatar,
      PhoneInput,
      ImageUpload,
      CountryAutocomplete,
      InvoiceStrategySelect,
      [Select.name]: Select,
      [Option.name]: Option,
    },
    props: {
      client: {
        type: Object,
        default: () => ({})
      },
      action: {
        type: Function,
        default: () => {
        },
        required: true
      },
      mode: {
        type: String,
        default: 'edit'
      }
    },
    data () {
      return {
        clientToEdit: {
          name: '',
          status: '',
          type: '',
          image: '',
          addresses: [{...emptyAddress}],
          phones: [{...emptyPhone}]
        },
        loading: false
      }
    },
    computed: {
      currencySymbol () {
        return this.$store.state.config.currencySymbol
      },
      clientStatuses () {
        return Object.keys(clientStatus).map(key => {
          let value = clientStatus[key]
          return {
            label: this.$t(`generic.clientStatuses.${value}`),
            value
          }
        })
      },
      clientTypes () {
        return Object.keys(clientType).map(key => {
          let value = clientType[key]
          return {
            label: this.$t(`generic.clientTypes.${value}`),
            value
          }
        })
      },
      modelValidations () {
        return {
          phone: {
            phoneNumber: true
          },
          vat_percentage: {
            min_value: 0,
            max_value: 100
          }
        }
      }
    },
    methods: {
      onAddressChange ({postal_code, country, locality, administrative_area_level_1, route, street_number, longitude, latitude}, place, index) {
        let address = this.clientToEdit.addresses[index]
        if (postal_code) {
          address.postal_code = postal_code
        }
        if (country) {
          address.country = country
          address.country_code = findCountryCode(place)
        }
        if (locality) {
          address.city = locality
        }
        if (administrative_area_level_1) {
          address.state_of_province = administrative_area_level_1
        }
        if (route) {
          address.address_line_1 = `${route} ${street_number}`
        }
      },
      onPhoneChange (data, index) {
        if (data.activeCountry) {
          this.clientToEdit.phones[index].country_code = data.activeCountry.iso2
        }
      },
      async validate () {
        return this.$validator.validateAll()
      },
      hasEmptyAddresses (addresses) {
        return addresses.every(a => this.isEmptyAddress(a))
      },
      isEmptyAddress (a) {
        return !a.address_line_1 && !a.city && !a.country_code && !a.postal_code
      },
      hasEmptyPhones (phones) {
        return phones.every(p => this.isEmptyPhone(p))
      },
      isEmptyPhone (p) {
        return !p.country_code && !p.phone
      },
      async submitForm () {
        let result = await this.validate()
        if (!result) return
        let data = {
          ...this.clientToEdit
        }

        if (data.vat_percentage != null) {
          // convert it to a positive number between 1 and 2 (e.g 20 -> 1.20)
          data.vat_percentage = data.vat_percetange / 100 + 1
        }
        data.addresses = data.addresses.filter(a => !this.isEmptyAddress(a))
        data.phones = data.phones.filter(p => !this.isEmptyPhone(p))
        try {
          this.loading = true
          await this.action(this.clientToEdit.uuid, data)
        } catch (e) {
          console.warn(e)
        } finally {
          this.loading = false
        }
      }
    },
    created () {
      this.$watch('client', newVal => {
        if (this.mode === 'add') return
        if (!newVal) return
        this.clientToEdit = JSON.parse(JSON.stringify(newVal))
        let financialAddresses = this.clientToEdit.addresses.filter(a => a.financial)
        let financialPhones = this.clientToEdit.phones.filter(p => p.financial)
        if (financialAddresses.length === 0) {
          this.clientToEdit.addresses.push({...emptyAddress})
        }
        if (financialPhones.length === 0) {
          this.clientToEdit.phones.push({...emptyPhone})
        }
      }, {immediate: true})
    }
  }
</script>
<style lang="scss">
  .client-form__row {
    display: flex;
    flex-wrap: wrap;

    .image-wrapper {
      width: 200px;
      display: flex;
      padding-left: 25px;
    }

    .form-wrapper {
      flex: 1;
      padding: 0 25px
    }
  }
</style>
